import React, { useEffect, useState } from "react";
import { Form, Button, Table, Radio, Image } from "antd";
import Banner from "../../../../views/components/sections/Banner";
import { getSingleImageUrl } from "../../../../lib/utils";
import TabsContain from "../../../../views/components/reuseComp/TabsContain";
import { useMessageApi } from "../../../../provider/AntdNotification";
import { useQueryState } from "nuqs";
import {
  useIndustryDetail,
  useIndustryList,
  useIndustrySave,
} from "../../../../hooks/api/industry/hook";
import { IndustryDetail, IndustryItem } from "../../../../types/api/industry";
import OverviewSection from "./overviewSection";
import IndustryChallenges from "./challengesSection";
import OurExpertise from "./expertiseSection";

interface Props {
  allowURLStateUpdate?: boolean;
  setBreadcrumb: React.Dispatch<React.SetStateAction<string>>;
}

const IndustryDetailTab: React.FC<Props> = ({
  allowURLStateUpdate = true,
  setBreadcrumb,
}) => {
  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };
  const [form] = Form.useForm();
  const message = useMessageApi();
  const [tab, setTab] = useQueryState("tab");
  const activeTab = tab || "Banner";

  const { data, isFetching: isListFetching } = useIndustryList({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });
  const industryList: IndustryItem[] = data?.data || [];

  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [selectedIndustry, setSelectedIndustry] =
    useState<IndustryDetail | null>(null);
  const [isSelectionDone, setIsSelectionDone] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [reviewMode, setReviewMode] = useState<"list" | "add" | "edit">("list");
  const [editingReviewIndex, setEditingReviewIndex] = useState<number | null>(
    null,
  );

  const qData = data?.qData ?? { page: 1, limit: 20 };
  const { data: industryDetail, isFetching: isDetailFetching } =
    useIndustryDetail(selectedSlug);
  const { mutate: saveIndustry, isPending } = useIndustrySave({ qData });

  useEffect(() => {
    if (industryDetail?.data) setSelectedIndustry(industryDetail.data);
  }, [industryDetail]);

  const handleChangeTab = (section: string) => {
    setTab(section);
    setIsEditing(false);
    setBreadcrumb(section);
  };

  const handleNext = () => {
    if (!selectedSlug)
      return message.warning("Please select an industry first");
    setIsSelectionDone(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingIndex(null);
  };

  const handleSave = async () => {
    if (!selectedIndustry) return;
    try {
      const values = await form.validateFields();
      let payload: IndustryDetail = { ...selectedIndustry };

      if (activeTab === "Banner") {
        payload.banner = {
          ...selectedIndustry.banner,
          ...values.banner,
          image:
            getSingleImageUrl(values?.banner?.image) ||
            selectedIndustry.banner?.image,
        };
      }

      if (activeTab === "Overview") {
        payload.overview = {
          ...selectedIndustry.overview,
          ...values.overview,
          image:
            getSingleImageUrl(values?.overview?.image) ||
            selectedIndustry.overview?.image,
        };
      }

      if (activeTab === "Challenges") {
        const existingChallenges = selectedIndustry.challenges;
        const existingContent = existingChallenges?.content ?? [];
        let updatedContent = [...existingContent];

        if (reviewMode === "add")
          updatedContent = [...existingContent, ...values.challenges.content];
        if (reviewMode === "edit" && editingReviewIndex !== null)
          updatedContent[editingReviewIndex] = values.challenges.content[0];

        payload.challenges = {
          heading:
            values.challenges.heading ?? existingChallenges?.heading ?? "",
          subtext:
            values.challenges.subtext ?? existingChallenges?.subtext ?? "",
          content: updatedContent,
        };
      }

      if (activeTab === "Our Expertise") {
        const existingData = selectedIndustry.expertise;
        const existingContent = existingData?.content ?? [];
        let updatedContent = [...existingContent];

        if (reviewMode === "add")
          updatedContent = [...existingContent, ...values.expertise.content];
        if (reviewMode === "edit" && editingReviewIndex !== null)
          updatedContent[editingReviewIndex] = values.expertise.content[0];

        payload.expertise = {
          heading: values.expertise.heading ?? existingData?.heading ?? "",
          subtext: values.expertise.subtext ?? existingData?.subtext ?? "",
          rightImage:
            values.expertise.rightImage ?? existingData?.rightImage ?? "",
          leftImage: [
            values.expertise.leftImage1 ?? existingData?.leftImage[0] ?? "",
            values.expertise.leftImage2 ?? existingData?.leftImage[1] ?? "",
          ],
          content: updatedContent,
        };
      }

      saveIndustry(payload, {
        onSuccess: () => {
          message.success("Saved successfully");
          setSelectedIndustry(payload);
          setReviewMode("list");
          setEditingReviewIndex(null);
          setIsEditing(false);
          form.resetFields();
        },
        onError: () => message.error("Failed to save"),
      });
    } catch (err) {
      console.log("Validation error:", err);
    }
  };

  type SectionField = "Challenges" | "Our Expertise";
  const handleDelete = (index: number, field: SectionField) => {
    if (!selectedIndustry) return;
    let payload: IndustryDetail = { ...selectedIndustry };

    if (field === "Challenges") {
      const currentChallenges = selectedIndustry.challenges;
      if (!currentChallenges) return;
      const updatedContent = currentChallenges.content.filter(
        (_, i) => i !== index,
      );
      payload = {
        ...selectedIndustry,
        challenges: { ...currentChallenges, content: updatedContent },
      };
    }

    if (field === "Our Expertise") {
      const currentExpertise = selectedIndustry.expertise;
      if (!currentExpertise) return;
      const updatedContent = currentExpertise.content.filter(
        (_, i) => i !== index,
      );
      payload = {
        ...selectedIndustry,
        expertise: { ...currentExpertise, content: updatedContent },
      };
    }

    saveIndustry(payload, {
      onSuccess: () => {
        message.success("Data deleted successfully");
        setSelectedIndustry(payload);
        setReviewMode("list");
        setEditingReviewIndex(null);
      },
      onError: () => message.error("Failed to delete"),
    });
  };

  const columns = [
    {
      title: "",
      align: "center" as const,
      render: (_: any, record: IndustryItem) => (
        <Radio
          checked={selectedSlug === record.slug}
          onChange={() => setSelectedSlug(record.slug)}
        />
      ),
    },
    {
      title: "Image",
      dataIndex: "image",
      align: "center" as const,
      render: (v: string) => <Image src={v} width={80} />,
    },
    {
      title: "Name",
      dataIndex: "heading",
      render: (text: string) => <span className="font-medium">{text}</span>,
    },
    {
      title: "",
      align: "center" as const,
      render: (_: any, record: IndustryItem) =>
        selectedSlug === record.slug ? (
          <Button
            type="dashed"
            className="bg-primary text-white px-9"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
          >
            Next
          </Button>
        ) : null,
    },
  ];

  const sections = ["Banner", "Overview", "Challenges", "Our Expertise"];

  return (
    <div>
      {!isSelectionDone && (
        <div className="p-4 rounded-md shadow-sm">
          <h2 className="text-lg font-semibold mb-3">
            Select an Industry to Edit
          </h2>
          <Table
            dataSource={industryList}
            columns={columns}
            rowKey="_id"
            pagination={false}
            bordered
            scroll={{ x: "max-content" }}
            className="custom-table-class"
            loading={isListFetching || isDetailFetching}
            onRow={(record: IndustryItem) => ({
              onClick: () => setSelectedSlug(record.slug),
            })}
            rowClassName={(record: IndustryItem) =>
              selectedSlug === record.slug
                ? "hover:bg-gray-100 cursor-pointer transition-all"
                : "hover:bg-gray-50 cursor-pointer"
            }
          />
        </div>
      )}

      {isSelectionDone && selectedIndustry && (
        <>
          <div className="flex mt-4 w-full">
            <Button
              type="dashed"
              color="default"
              className="mb-4 bg-primary text-white w-full"
              onClick={() => {
                setIsSelectionDone(false);
                setSelectedSlug("");
              }}
            >
              Select Another Industry
            </Button>
          </div>

          <h2 className="text-lg font-semibold mb-4">
            Editing Industry:{" "}
            <span className="text-primary">
              {selectedIndustry?.banner?.heading}
            </span>
          </h2>

          <TabsContain
            sections={sections}
            activeTab={activeTab}
            handleChangeTab={handleChangeTab}
          />

          {activeTab === "Banner" && (
            <Banner
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              bannerData={selectedIndustry?.banner}
              loading={isPending}
            />
          )}
          {activeTab === "Overview" && (
            <OverviewSection
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              overviewData={selectedIndustry?.overview}
              loading={isPending}
            />
          )}
          {activeTab === "Challenges" && (
            <IndustryChallenges
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              challengeData={selectedIndustry?.challenges}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}
          {activeTab === "Our Expertise" && (
            <OurExpertise
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              expertiseData={selectedIndustry?.expertise}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}
        </>
      )}
    </div>
  );
};

export default IndustryDetailTab;
